import { REEL_STAGES } from './contract';
import { mapCaptionCapabilities, mapCaptionStyle } from './style';
import type {
  CaptionCue,
  CaptionCueResponse,
  ClipDetail,
  ClipDetailResponse,
  ClipFailure,
  ClipPlatform,
  ClipRowResponse,
  ClipsListResponse,
  ClipsPage,
  ClipStatus,
  ClipStatusCounts,
  LibraryClip,
  PostingHistoryEntry,
  PostResponse,
  RerenderClipResponse,
  RerenderClipResult,
  ReelsPageResponse,
  ReelsPageView,
  ReelStage,
  CaptionStyle,
  ReelTask,
  StartReelsResponse,
  StartReelsResult,
  TaskResponse,
} from './types';

const PLATFORMS: ClipPlatform[] = ['instagram', 'facebook', 'youtube', 'twitter', 'tiktok'];

/** The contract's stage; an unknown or missing one (a task that has not started) reads as `preparing`. */
export const mapReelsStage = (stage: string | null | undefined): ReelStage =>
  (REEL_STAGES as readonly string[]).includes(stage as string) ? (stage as ReelStage) : 'preparing';

const percent = (value: number | null | undefined) => Math.max(0, Math.min(100, Math.round(value ?? 0)));

/**
 * Status of a Library row as the UI reads it: `posted` is published, `publish_failed` a post that did not go out, and
 * a task's `failed` the reel never being made.
 */
export function mapClipStatus(
  status: string | null | undefined,
  kind: 'clip' | 'task'
): { status: ClipStatus; failure?: ClipFailure } {
  switch (status) {
    case 'posted':
      return { status: 'published' };
    case 'scheduled':
      return { status: 'scheduled' };
    case 'publish_failed':
      return { status: 'failed', failure: 'publish' };
    case 'failed':
      return { status: 'failed', failure: kind === 'task' ? 'generation' : 'publish' };
    case 'generating':
      return { status: 'generating' };
    case 'ready':
      return { status: 'ready' };
    default:
      return { status: kind === 'task' ? 'generating' : 'ready' };
  }
}

export function mapReelsTask(data: TaskResponse): ReelTask {
  let status: ReelTask['status'] = 'generating';
  if (data.status === 'completed') status = 'ready';
  else if (data.status === 'failed') status = 'failed';
  return {
    id: data.id,
    kind: data.taskKind === 'rerender' ? 'rerender' : 'generate',
    batchId: data.batchId ?? null,
    contentId: data.contentId ?? '',
    sourceTitle: data.sourceTitle ?? '',
    status,
    stage: mapReelsStage(data.stage),
    progress: percent(data.progress),
    failureCode: status === 'failed' ? (data.failedReason ?? null) : null,
    refunded: !!data.refunded,
    creditsCharged: data.creditsCharged ?? null,
    momentRank: typeof data.momentRank === 'number' ? data.momentRank : null,
    clipId: data.clipId ?? null,
    warnings: (data.warnings ?? []).filter((warning): warning is string => typeof warning === 'string' && !!warning),
    createdAt: data.createdAt,
  };
}

/** A task as a Library row, with its place in the click when the click's size is known. */
export function taskAsClip(task: ReelTask, batchSize: number | null = null): LibraryClip {
  return {
    id: task.id,
    kind: 'task',
    clipId: task.clipId,
    taskId: task.id,
    type: 'pps',
    status: task.status === 'failed' ? 'failed' : 'generating',
    failure: task.status === 'failed' ? 'generation' : undefined,
    title: null,
    caption: '',
    hashtags: '',
    transcript: '',
    source: { id: task.contentId, title: task.sourceTitle, artworkUrl: '' },
    durationSeconds: null,
    timeRange: null,
    createdAt: task.createdAt,
    thumbnailUrl: null,
    videoUrl: null,
    aiGenerated: true,
    progress: task.progress,
    stage: task.stage,
    failureCode: task.failureCode ?? undefined,
    refunded: task.refunded,
    warnings: task.warnings,
    batch: task.batchId ? { id: task.batchId, index: task.momentRank, size: batchSize } : undefined,
  };
}

export function mapClip(data: ClipRowResponse): LibraryClip {
  const kind = data.kind === 'task' ? 'task' : 'clip';
  const { status, failure } = mapClipStatus(data.status, kind);
  const { task, latestPost } = data;
  const hasTimeRange = typeof data.timeRangeStart === 'number' && typeof data.timeRangeEnd === 'number';
  const inFlight = status === 'generating';

  return {
    id: data.id,
    kind,
    clipId: kind === 'clip' ? data.id : null,
    taskId: kind === 'task' ? data.id : null,
    type: data.type === 'pps' ? 'pps' : 'opus',
    status,
    failure,
    title: data.title?.trim() || null,
    caption: '',
    hashtags: '',
    transcript: '',
    source: { id: data.source?.contentId ?? '', title: data.source?.title ?? '', artworkUrl: '' },
    durationSeconds: data.durationSeconds ? Math.round(data.durationSeconds) : null,
    timeRange: hasTimeRange ? { start: data.timeRangeStart as number, end: data.timeRangeEnd as number } : null,
    createdAt: data.createdAt,
    thumbnailUrl: data.posterUrl ?? null,
    videoUrl: data.videoUrl ?? null,
    aiGenerated: data.aiGenerated ?? data.type === 'pps',
    progress: inFlight && task ? percent(task.progress) : undefined,
    stage: inFlight ? mapReelsStage(task?.stage) : undefined,
    failureCode: failure === 'generation' ? (task?.failedReason ?? undefined) : undefined,
    scheduledFor: status === 'scheduled' ? (latestPost?.publishedAt ?? undefined) : undefined,
    batch: task?.batchId ? { id: task.batchId, index: null, size: null } : undefined,
    socialPostId: latestPost?.id ?? null,
  };
}

const ZERO_COUNTS: ClipStatusCounts = { total: 0, pps: 0, opus: 0, posted: 0, generating: 0, failed: 0 };

/**
 * Rows carry no moment rank, so a click's reels still being made are numbered in the order the page lists them
 * ("Reel 1 of 2"); a click with one row left is just a new reel.
 */
function numberBatches(clips: LibraryClip[]): LibraryClip[] {
  const batches = new Map<string, LibraryClip[]>();
  clips.forEach((clip) => {
    if (clip.kind === 'task' && clip.batch) batches.set(clip.batch.id, [...(batches.get(clip.batch.id) ?? []), clip]);
  });
  return clips.map((clip) => {
    const batch = clip.kind === 'task' && clip.batch ? (batches.get(clip.batch.id) ?? []) : [];
    if (batch.length < 2 || !clip.batch) return clip;
    return { ...clip, batch: { ...clip.batch, index: batch.indexOf(clip) + 1, size: batch.length } };
  });
}

export function mapClipsPage(response: ClipsListResponse): ClipsPage {
  const counts = response.statusCounts;
  return {
    clips: numberBatches((response.rows ?? []).map(mapClip)),
    nextCursor: response.nextCursor ?? null,
    total: typeof response.total === 'number' ? response.total : null,
    statusCounts: counts ? { ...ZERO_COUNTS, ...counts } : null,
  };
}

export const mapCaptionCue = (cue: CaptionCueResponse, index: number): CaptionCue => ({
  id: `cue-${index + 1}`,
  start: cue.startSeconds,
  end: cue.endSeconds,
  text: cue.text,
  ...(Array.isArray(cue.words) ? { words: cue.words } : {}),
});

/** The re-render's captions: a cue keeps PPS's `words` only while the leader has not edited it. */
export const toCaptionCuesPayload = (cues: CaptionCue[]): CaptionCueResponse[] =>
  cues.map(({ start, end, text, words }) => ({
    text: text.trim(),
    startSeconds: start,
    endSeconds: end,
    ...(words ? { words } : {}),
  }));

const POST_PLATFORM_STATUS: Record<string, PostingHistoryEntry['status']> = {
  published: 'published',
  failed: 'failed',
  pending: 'pending',
};

/** One history line per outlet of every post; a scheduled post's outlets read as scheduled. */
function postingHistoryOf(posts: PostResponse[]): PostingHistoryEntry[] {
  return posts.flatMap((post) =>
    post.platforms.map((platform) => ({
      platform: platform.platform,
      status: post.status === 'scheduled' ? 'scheduled' : (POST_PLATFORM_STATUS[platform.status] ?? 'pending'),
      at: platform.publishedAt ?? post.publishedAt ?? null,
      postUrl: null,
      error: platform.failedReason ?? (platform.status === 'failed' ? post.failedReason : null),
    }))
  );
}

const isPlatform = (platform: string): platform is ClipPlatform => PLATFORMS.includes(platform as ClipPlatform);

/**
 * GET …/clips/:clipId. A reel with a re-render running reads as `rerendering`, with that task's progress and
 * warnings; otherwise the warnings of the last re-render stay on the reel as notes.
 */
export function mapClipDetail(data: ClipDetailResponse, logoUrl: string | null = null): ClipDetail {
  const type = data.type === 'pps' ? 'pps' : 'opus';
  const { status: listed, failure } = mapClipStatus(data.status, 'clip');
  const activeTask = data.rerender?.activeTask ? mapReelsTask(data.rerender.activeTask) : null;
  const lastTask = data.rerender?.lastTask ? mapReelsTask(data.rerender.lastTask) : null;
  const status = activeTask ? 'rerendering' : listed;
  const posts = data.posts ?? [];
  const latest = posts[0];
  const style: CaptionStyle | null =
    type === 'pps' && data.captionStyle ? mapCaptionStyle(data.captionStyle, undefined, logoUrl) : null;

  return {
    id: data.id,
    kind: 'clip',
    clipId: data.id,
    taskId: activeTask?.id ?? null,
    type,
    status,
    failure: activeTask ? undefined : failure,
    title: data.title?.trim() || null,
    caption: data.description?.trim() ?? '',
    hashtags: (data.hashtags ?? '').trim(),
    transcript: data.transcript ?? '',
    source: { id: data.source?.contentId ?? '', title: data.source?.title ?? '', artworkUrl: '' },
    durationSeconds: data.durationSeconds ? Math.round(data.durationSeconds) : null,
    timeRange:
      typeof data.timeRangeStart === 'number' && typeof data.timeRangeEnd === 'number'
        ? { start: data.timeRangeStart, end: data.timeRangeEnd }
        : null,
    createdAt: data.createdAt,
    thumbnailUrl: data.posterUrl ?? null,
    videoUrl: data.videoUrl ?? null,
    aiGenerated: data.aiGenerated ?? type === 'pps',
    progress: activeTask?.progress,
    stage: activeTask?.stage,
    failedReason: failure === 'publish' ? (latest?.failedReason ?? undefined) : undefined,
    warnings: (activeTask ?? lastTask)?.warnings ?? [],
    postedPlatforms: posts
      .filter((post) => post.status === 'posted' || post.status === 'scheduled')
      .flatMap((post) => post.platforms.filter((platform) => platform.status !== 'failed').map((p) => p.platform))
      .filter(isPlatform),
    scheduledFor: listed === 'scheduled' ? (latest?.publishedAt ?? undefined) : undefined,
    socialPostId: latest?.id ?? null,
    captions: (data.captions ?? []).map(mapCaptionCue),
    captionTimingEditable: data.captionTimingEditable === true,
    style,
    postingHistory: postingHistoryOf(posts),
    rerender: {
      available: !!data.rerender?.available,
      credits: data.rerender?.credits ?? null,
      activeTask,
      lastTask,
    },
    downloadFileName: data.downloadFileName ?? null,
  };
}

export function mapReelsPage(data: ReelsPageResponse): ReelsPageView {
  return {
    available: data.available !== false,
    unavailableReason: data.unavailableReason ?? null,
    eligible: data.eligible !== false,
    ineligibleReason: data.ineligibleReason ?? null,
    price: data.price ?? null,
    latestBatch: data.latestBatch
      ? {
          batchId: data.latestBatch.batchId,
          active: !!data.latestBatch.active,
          tasks: (data.latestBatch.tasks ?? []).map(mapReelsTask),
        }
      : null,
    suggestedTags: (data.suggestedTags ?? []).filter(Boolean),
    capabilities: mapCaptionCapabilities(data.capabilities),
  };
}

export const mapStartReels = (data: StartReelsResponse): StartReelsResult => ({
  batchId: data.batchId ?? null,
  replayed: !!data.replayed,
  tasks: (data.tasks ?? []).map(mapReelsTask),
});

export const mapRerenderClip = (data: RerenderClipResponse): RerenderClipResult => ({
  replayed: !!data.replayed,
  task: data.task ? mapReelsTask(data.task) : null,
});
